import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSellerStore } from '@/store/seller';
import { sellerService } from '@/services/seller';
import { restaurantService } from '@/services/restaurant';
import { useToast } from '@/components/common/Toast';
import { SellerPageContainer } from '@/components/seller/layout/SellerPageContainer';
import { SalesMetricCard } from '@/components/seller/dashboard/SalesMetricCard';
import { ReviewNotificationCard } from '@/components/seller/dashboard/ReviewNotificationCard';
import { OrderStatusTabs } from '@/components/seller/orders/OrderStatusTabs';
import { OrderList } from '@/components/seller/orders/OrderList';
import { OrderReadyConfirmModal } from '@/components/seller/orders/OrderReadyConfirmModal';
import { OrderDetailModal } from '@/components/seller/modals/OrderDetailModal';
import { supabase } from '@/lib/supabase';
import type { OrderStatus } from '@/types';
import type { OrderWithItems } from '@/store/seller';

export function SellerHomePage() {
  const navigate = useNavigate();
  const { showToast, ToastContainer } = useToast();
  
  const {
    salesMetrics,
    reviewCount,
    businessStatus,
    orders,
    activeOrderTab,
    loading,
    error,
    setSalesMetrics,
    setReviewCount,
    setBusinessStatus,
    setOrders,
    setOrderCounts,
    setActiveOrderTab,
    setLoading,
    setError,
    getOrderCount
  } = useSellerStore();

  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [restaurantName, setRestaurantName] = useState<string>('');
  const [showReadyModal, setShowReadyModal] = useState<boolean>(false);
  const [selectedOrderForReady, setSelectedOrderForReady] = useState<string | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
  const [selectedOrderForDetail, setSelectedOrderForDetail] = useState<OrderWithItems | null>(null);
  const [showOnlyCompleted, setShowOnlyCompleted] = useState<boolean>(() => {
    // localStorage에서 필터 상태 복원 (기본값: false로 모든 주문 표시)
    const saved = localStorage.getItem('seller_show_only_completed_filter');
    return saved === 'true'; // 기본값을 false로 설정
  });

  // 알림 추적 상태
  const [notifiedOrders] = useState<Set<string>>(new Set());
  const [reminderTimers] = useState<Map<string, NodeJS.Timeout>>(new Map());

  // 필터 상태 저장
  useEffect(() => {
    localStorage.setItem('seller_show_only_completed_filter', showOnlyCompleted.toString());
  }, [showOnlyCompleted]);

  // 브라우저 알림 권한 요청 (페이지 로드 1초 후)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      setTimeout(() => {
        Notification.requestPermission().then(permission => {
          console.log('알림 권한:', permission);
        });
      }, 1000);
    }
  }, []);

  // 알림 전송 헬퍼 함수
  const sendNotification = (title: string, body: string, soundFile: string) => {
    if (Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/logo192.png',
        badge: '/logo192.png',
        tag: `order-${Date.now()}`,
        requireInteraction: true
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
    
    const audio = new Audio(soundFile);
    audio.play().catch(e => console.log('Audio play failed:', e));
  };

  // 재알림 함수
  const sendReminderNotification = async (orderId: string) => {
    // 주문이 여전히 PENDING 상태인지 확인
    if (!restaurantId) return;
    
    try {
      const ordersResult = await sellerService.getOrdersByStatus(restaurantId, 'PENDING');
      if (ordersResult.success && ordersResult.data) {
        const order = ordersResult.data.find(o => o.id === orderId);
        if (order && order.status === 'PENDING') {
          sendNotification(
            '⏰ 미처리 주문이 있습니다!',
            `주문번호: ${order.order_number || order.id.slice(0, 8)}`,
            '/sounds/seller/new_order.mp3'
          );
        }
      }
    } catch (error) {
      console.error('재알림 중 오류:', error);
    }
    
    // 타이머 정리
    reminderTimers.delete(orderId);
  };

  // 타이머 정리 함수
  const clearOrderTimer = (orderId: string) => {
    const timer = reminderTimers.get(orderId);
    if (timer) {
      clearTimeout(timer);
      reminderTimers.delete(orderId);
    }
    notifiedOrders.delete(orderId);
  };

  // 레스토랑 ID 가져오기 및 대시보드 데이터 로드
  useEffect(() => {
    const initializeSellerData = async () => {
      setLoading(true);
      setError(null);

      try {
        // 1. 먼저 현재 판매자의 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }

        const currentRestaurantId = restaurantResult.restaurantId;
        setRestaurantId(currentRestaurantId);

        // 2. 매장 정보 가져오기
        const restaurantInfoResult = await restaurantService.getRestaurantById(currentRestaurantId);
        if (restaurantInfoResult.success && restaurantInfoResult.data) {
          setRestaurantName(restaurantInfoResult.data.name);
        }

        // 3. 대시보드 데이터 가져오기
        const dashboardResult = await sellerService.getDashboardData(currentRestaurantId);
        if (dashboardResult.success && dashboardResult.data) {
          setSalesMetrics(dashboardResult.data.salesMetrics);
          setReviewCount(dashboardResult.data.reviewCount);
          setBusinessStatus(dashboardResult.data.businessStatus);
        } else {
          setError(dashboardResult.error || '대시보드 데이터를 불러오는데 실패했습니다');
        }

        // 3. 주문 카운트 가져오기
        const countsResult = await sellerService.getOrderCounts(currentRestaurantId);
        if (countsResult.success && countsResult.data) {
          // 실제 카운트를 저장
          setOrderCounts(countsResult.data);
          
          // 각 상태별로 빈 배열로 초기화 (실제 주문은 탭 클릭 시 로드)
          Object.keys(countsResult.data).forEach(status => {
            setOrders(status as OrderStatus, []);
          });
        }

        // 4. 활성 탭의 주문 데이터 로드 (persist된 활성 탭 존중)
        await loadOrdersForTab(activeOrderTab, currentRestaurantId);
        
      } catch (error) {
        console.error('Seller data initialization error:', error);
        setError('데이터를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    initializeSellerData();
  }, []);

  // 복귀/포커스 시 현재 탭 자동 재로딩 (과도 호출 방지용 쓰로틀)
  const lastReloadRef = useRef<number>(0);
  useEffect(() => {
    const maybeReload = async () => {
      const now = Date.now();
      if (now - lastReloadRef.current < 2000) return; // 2초 쓰로틀
      if (!restaurantId) return;
      lastReloadRef.current = now;
      await loadOrdersForTab(activeOrderTab);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        maybeReload();
      }
    };
    const onFocus = () => { maybeReload(); };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [activeOrderTab, restaurantId]);

  // Supabase 실시간 구독
  useEffect(() => {
    if (!restaurantId) return;
    
    const subscription = supabase
      .channel(`seller-${restaurantId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${restaurantId}`
      }, (payload) => {
        const order = payload.new as any;
        if (order.payment_status === 'PAID' && order.status === 'PENDING') {
          const orderId = order.id;
          const orderNumber = order.order_number || order.id.slice(0, 8);
          
          // 첫 번째 알림이 아직 발송되지 않은 경우만
          if (!notifiedOrders.has(orderId)) {
            notifiedOrders.add(orderId);
            
            // 첫 번째 알림
            sendNotification(
              '🔔 새 주문이 들어왔습니다!',
              `주문번호: ${orderNumber}`,
              '/sounds/seller/new_order.mp3'
            );
            
            // 2분 후 재알림 설정
            const timer = setTimeout(() => {
              sendReminderNotification(orderId);
            }, 120000); // 2분
            
            reminderTimers.set(orderId, timer);
          }
          
          loadOrdersForTab('PENDING');
        }
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'orders',
        filter: `restaurant_id=eq.${restaurantId}`
      }, (payload) => {
        const oldOrder = payload.old as any;
        const newOrder = payload.new as any;
        
        // 결제 완료 알림
        if (oldOrder.payment_status !== 'PAID' && 
            newOrder.payment_status === 'PAID' && 
            newOrder.status === 'PENDING') {
          const orderId = newOrder.id;
          const orderNumber = newOrder.order_number || newOrder.id.slice(0, 8);
          
          // 첫 번째 알림이 아직 발송되지 않은 경우만
          if (!notifiedOrders.has(orderId)) {
            notifiedOrders.add(orderId);
            
            // 첫 번째 알림
            sendNotification(
              '🔔 새 주문이 들어왔습니다!',
              `주문번호: ${orderNumber}`,
              '/sounds/seller/new_order.mp3'
            );
            
            // 2분 후 재알림 설정
            const timer = setTimeout(() => {
              sendReminderNotification(orderId);
            }, 120000); // 2분
            
            reminderTimers.set(orderId, timer);
          }
          
          loadOrdersForTab('PENDING');
        }
        
        // 주문 상태 변경 시 타이머 정리
        if (oldOrder.status === 'PENDING' && newOrder.status !== 'PENDING') {
          clearOrderTimer(newOrder.id);
        }
        
        // 취소 알림 (PENDING/ACCEPTED → CANCELLED)
        if (newOrder.status === 'CANCELLED' && 
            newOrder.payment_status === 'PAID' &&
            (oldOrder.status === 'PENDING' || oldOrder.status === 'ACCEPTED')) {
          sendNotification(
            '❌ 주문이 취소되었습니다',
            oldOrder.status === 'PENDING' ? '수락 전 취소' : '준비 중 취소',
            '/sounds/seller/order_cancelled.mp3'
          );
          loadOrdersForTab('CANCELLED');
          loadOrdersForTab(oldOrder.status);
        }
        
        // 환불 알림 (READY/COMPLETED → CANCELLED)
        if (newOrder.status === 'CANCELLED' && 
            (oldOrder.status === 'READY' || oldOrder.status === 'COMPLETED')) {
          sendNotification(
            '💰 주문이 환불되었습니다',
            oldOrder.status === 'READY' ? '준비완료 → 환불' : '완료 → 환불',
            '/sounds/seller/order_cancelled.mp3'
          );
          
          // 완료/종료 탭(READY) 리로드
          loadOrdersForTab('READY');
        }
      })
      .subscribe();
      
    return () => {
      supabase.removeChannel(subscription);
    };
  }, [restaurantId]);

  // 컴포넌트 언마운트 시 모든 타이머 정리
  useEffect(() => {
    return () => {
      reminderTimers.forEach((timer) => {
        clearTimeout(timer);
      });
      reminderTimers.clear();
      notifiedOrders.clear();
    };
  }, []);

  // 특정 탭의 주문 데이터 로드
  const loadOrdersForTab = async (status: OrderStatus, targetRestaurantId?: string) => {
    const currentRestaurantId = targetRestaurantId || restaurantId;
    
    if (!currentRestaurantId) {
      showToast('레스토랑 정보가 없습니다');
      return;
    }

    try {
      // READY 탭의 경우 복합 상태 조회
      if (status === 'READY') {
        const statuses: OrderStatus[] = ['READY', 'COMPLETED', 'CANCELLED', 'NOSHOW'];
        const ordersResult = await sellerService.getMultipleStatusOrders(currentRestaurantId, statuses);
        
        if (ordersResult.success && ordersResult.data) {
          // 필터 적용 로직
          let filteredOrders = ordersResult.data;
          
          if (showOnlyCompleted) {
            // 체크박스 체크됨: 정상 완료된 주문만 표시 (READY, COMPLETED)
            filteredOrders = ordersResult.data.filter(order => 
              order.status === 'READY' || order.status === 'COMPLETED'
            );
          } else {
            // 체크박스 해제됨: 모든 주문 표시 (READY, COMPLETED, CANCELLED, NOSHOW)
            filteredOrders = ordersResult.data;
          }
          
          setOrders(status, filteredOrders);
          // 카운트는 초기화 시에만 설정하고 실제 데이터 로드 시에는 업데이트하지 않음
        } else {
          showToast(ordersResult.error || '주문 목록을 불러오는데 실패했습니다');
        }
      } else {
        // 기존 로직 유지
        const ordersResult = await sellerService.getOrdersByStatus(currentRestaurantId, status);
        if (ordersResult.success && ordersResult.data) {
          setOrders(status, ordersResult.data);
          // 카운트는 초기화 시에만 설정하고 실제 데이터 로드 시에는 업데이트하지 않음
        } else {
          showToast(ordersResult.error || '주문 목록을 불러오는데 실패했습니다');
        }
      }
    } catch (error) {
      console.error('Orders load error:', error);
      showToast('주문 목록을 불러오는 중 오류가 발생했습니다');
    }
  };

  // 탭 변경 핸들러
  const handleTabChange = async (tab: OrderStatus) => {
    setActiveOrderTab(tab);
    
    // 해당 탭의 주문이 아직 로드되지 않았으면 로드
    if (orders[tab].length === 0) {
      await loadOrdersForTab(tab);
    }
  };

  // 주문 액션 핸들러
  const handleOrderAction = async (orderId: string, action: string) => {
    if (action === 'ready') {
      // 준비완료 확인 모달 표시
      setSelectedOrderForReady(orderId);
      setShowReadyModal(true);
    } else if (action === 'complete') {
      // 수령완료 처리 (READY → COMPLETED)
      if (confirm('고객이 상품을 수령했습니까?\n수령완료 처리하시겠습니까?')) {
        const result = await sellerService.completeOrder(orderId);
        if (result.success) {
          showToast('수령완료 처리되었습니다');
          await loadOrdersForTab('READY');
        } else {
          showToast('처리 중 오류가 발생했습니다');
        }
      }
    } else if (action === 'refund') {
      // 환불 페이지로 바로 이동 (ACCEPTED, READY, COMPLETED 상태 모두 동일하게 처리)
      navigate(`/seller/orders/${orderId}/refund`);
    } else if (action === 'accept') {
      // 주문 수락 페이지로 이동
      navigate(`/seller/orders/${orderId}/accept`);
    } else if (action === 'reject') {
      // 주문 거부 페이지로 이동
      navigate(`/seller/orders/${orderId}/reject`);
    }
  };

  // 주문 상세 핸들러
  const handleOrderDetail = (order: OrderWithItems) => {
    setSelectedOrderForDetail(order);
    setShowDetailModal(true);
  };

  // 준비완료 확인 처리
  const handleConfirmReady = async () => {
    if (!selectedOrderForReady) return;
    
    const result = await sellerService.markOrderReady(selectedOrderForReady);
    if (result.success) {
      showToast('준비완료 처리되었습니다');
      await loadOrdersForTab('ACCEPTED');
      await loadOrdersForTab('READY');
      setShowReadyModal(false);
      setSelectedOrderForReady(null);
    } else {
      showToast('처리 중 오류가 발생했습니다');
    }
  };

  // 리뷰 페이지로 이동
  const handleReviewClick = () => {
    navigate('/seller/store-reviews');
  };

  // 필터 변경 핸들러
  const handleFilterChange = async (checked: boolean) => {
    setShowOnlyCompleted(checked);
    // 필터 변경 시 READY 탭이 활성화되어 있으면 데이터 리로드
    if (activeOrderTab === 'READY') {
      await loadOrdersForTab('READY');
    }
  };

  // 주문 카운트 가져오기
  const getOrderCounts = (): Record<OrderStatus, number> => {
    return {
      PENDING: getOrderCount('PENDING'),
      ACCEPTED: getOrderCount('ACCEPTED'),
      READY: getOrderCount('READY'),
      COMPLETED: getOrderCount('COMPLETED'),
      CANCELLED: getOrderCount('CANCELLED'),
      NOSHOW: getOrderCount('NOSHOW')
    };
  };

  // 로딩 상태
  if (loading && !salesMetrics) {
    return (
      <SellerPageContainer activeNavId="home">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">판매자 정보를 불러오는 중...</p>
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  // 오류 상태
  if (error) {
    return (
      <SellerPageContainer activeNavId="home">
        <div className="min-h-screen bg-gray-100 flex items-center justify-center">
          <div className="text-center">
            <div className="text-red-500 text-4xl mb-4">⚠️</div>
            <p className="text-gray-600 mb-4">{error}</p>
            {error === '로그인이 필요합니다' ? (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => (window.location.href = '/login')}
                  className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
                >
                  로그인
                </button>
                <button
                  onClick={() => (window.location.href = '/')}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  홈으로
                </button>
              </div>
            ) : (
              <button
                onClick={() => window.location.reload()}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600"
              >
                다시 시도
              </button>
            )}
          </div>
        </div>
      </SellerPageContainer>
    );
  }

  return (
    <SellerPageContainer activeNavId="home">
      <div className="pb-4">
        {/* 페이지 제목 */}
        <h1 className="text-[24px] font-bold text-gray-800 mb-0 text-center">홈</h1>

        {/* 매출 정보 카드 */}
        <SalesMetricCard salesMetrics={salesMetrics} restaurantName={restaurantName} loading={loading} />

        {/* 리뷰 알림 및 영업 상태 */}
        <ReviewNotificationCard
          reviewCount={reviewCount}
          businessStatus={businessStatus}
          onReviewClick={handleReviewClick}
          loading={loading}
        />

        {/* 주문 상태별 탭 */}
        <OrderStatusTabs
          activeTab={activeOrderTab}
          onTabChange={handleTabChange}
          orderCounts={getOrderCounts()}
          loading={loading}
        />

        {/* 주문 목록 */}
        <OrderList
          orders={orders[activeOrderTab]}
          activeTab={activeOrderTab}
          onActionClick={handleOrderAction}
          onDetailClick={handleOrderDetail}
          showFilter={activeOrderTab === 'READY'}
          filterChecked={showOnlyCompleted}
          onFilterChange={handleFilterChange}
          loading={loading}
        />
      </div>

      {/* 준비완료 확인 모달 */}
      <OrderReadyConfirmModal
        isOpen={showReadyModal}
        order={selectedOrderForReady ? orders.ACCEPTED.find(o => o.id === selectedOrderForReady) || null : null}
        onConfirm={handleConfirmReady}
        onCancel={async () => {
          setShowReadyModal(false);
          setSelectedOrderForReady(null);
          await loadOrdersForTab(activeOrderTab);
        }}
      />

      {/* 주문 상세 모달 */}
      <OrderDetailModal
        isOpen={showDetailModal}
        order={selectedOrderForDetail}
        onClose={async () => {
          setShowDetailModal(false);
          setSelectedOrderForDetail(null);
          await loadOrdersForTab(activeOrderTab);
        }}
      />

      <ToastContainer />
    </SellerPageContainer>
  );
}
